import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { sortBy } from 'lib/array/arrayUtils'
import useQueryParams from 'hooks/useQueryParams'
import { pluralizeToString } from 'lib/string/pluralize'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BedbankRoomOptionMobile from '../BedbankRoomOptionMobile'
import { GOOGLE_ADS_UTM_CONTENT } from 'constants/bedbank'
import BodyText from 'components/Luxkit/Typography/BodyText'
import BedbankMobileStickyHeaderFilters from '../BedbankMobileStickyHeaderFilters'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
`

interface Props {
  checkInDate?: string;
  checkOutDate?: string;
  offer: App.BedbankOffer;
  onClose: () => void;
  onBack?: () => void;
}

function BedbankRoomSelectionModal(props: Props) {
  const {
    offer,
    checkInDate,
    checkOutDate,
    onClose,
    onBack,
  } = props

  const query = useQueryParams()
  const roomTypeId = query.get('roomTypeId')
  const utmContent = query.get('utm_content')

  const [packageViews, fetchingRates] = useBedbankPackageViews(offer)
  const validCheckinCheckoutDates = !!(checkInDate && checkOutDate)

  const hasPromotions = offer.promotions.length > 0
  const showAll = packageViews.filter(p => p.available).length === 0

  const sortedPackageViews = useMemo(() => {
    return sortBy(packageViews, pkg => {
      if (pkg.cheapestRate) {
        return pkg.cheapestRate.totals.inclusive + (pkg.cheapestRate.totals.propertyFees ?? 0)
      } else if (hasPromotions) {
        return Number.MAX_SAFE_INTEGER
      }

      if (validCheckinCheckoutDates) {
        if (!pkg.capacityValid) {
          return Number.MAX_SAFE_INTEGER - 1
        }

        return Number.MAX_SAFE_INTEGER
      }

      if (pkg.capacityValid) {
        return Number.MIN_SAFE_INTEGER
      }

      return Number.MAX_SAFE_INTEGER
    }, 'asc')
  }, [packageViews, validCheckinCheckoutDates, hasPromotions])

  const sortedPackages = useMemo(() => {
    const highlightedPackage = sortedPackageViews.find(p => p.package.roomId === roomTypeId)
    if (utmContent === GOOGLE_ADS_UTM_CONTENT && highlightedPackage) {
      return [
        highlightedPackage,
        sortedPackageViews.find(p => p.package.roomId !== roomTypeId),
      ].filter((p): p is App.BedbankOfferPackageView => !!p) // this fix type inference
    }
    else {
      return sortedPackageViews
    }
  }, [sortedPackageViews, utmContent, roomTypeId])

  const displayablePackages = useMemo(() => sortedPackages.filter(
    packageView => (packageView.show && packageView.available) || (packageView.show && showAll),
  ), [sortedPackages, showAll])

  return (
    <>
      <ModalHeader
        title="Select a room"
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <BedbankMobileStickyHeaderFilters />
      </ModalHeader>
      <ModalBody>
        <Group direction="vertical">
          {displayablePackages.length > 1 && (
            <OptionsContainer>
              <BodyText variant="small" colour="neutral-three">{pluralizeToString('room type', displayablePackages.length)} available</BodyText>
            </OptionsContainer>
          )}
          <Pane background="neutral-six">
            <VerticalSpacer gap={8}>
              {displayablePackages.map(packageView => <BedbankRoomOptionMobile
                key={packageView.package.roomId}
                offer={offer}
                packageView={packageView}
                fetchingRates={fetchingRates}
                isHighlighted={utmContent === GOOGLE_ADS_UTM_CONTENT && roomTypeId === packageView.package.roomId}
                detailsModalMode="room-only"
              />)}
            </VerticalSpacer>
          </Pane>
        </Group>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState) => ({
  offer: state.bedbankOffer!,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
}), BedbankRoomSelectionModal)
